import { createRoute } from "@tanstack/react-router";
import { lazy, Suspense, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Route as TenantLayoutRoute } from "./_layout";
import { Button } from "@/components/base/buttons/button";
import { Card, CardBody, CardHeader } from "@/components/application/card/card";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Modal } from "@/components/application/modals/modal-controlled";
import { StatCard } from "@/components/application/metrics/stat-card";
import { ActionCardKolikDlulis } from "../../components/custom/ActionCardKolikDlulis";
import { BottomSheet } from "../../components/custom/BottomSheet";
import { ClosedMonthsTable } from "../../components/custom/ClosedMonthsTable";
import { SpzMobileBreakdownTable } from "../../components/custom/SpzMobileBreakdownTable";
import { TimeRangeSelector } from "../../components/custom/TimeRangeSelector";
// Recharts (charts-vendor ~381kB) je heavy a používá ho jen tenant dashboard +
// month-detail; lazy import drží initial bundle na /login a /index pod limitem.
const SpzBarChart = lazy(() =>
  import("../../components/custom/SpzBarChart").then((m) => ({ default: m.SpzBarChart })),
);
const TimelineChart = lazy(() =>
  import("../../components/custom/TimelineChart").then((m) => ({ default: m.TimelineChart })),
);
import { ExportFormatRadioGroup } from "../../components/tenant/ExportFormatRadioGroup";
import { useAuthStore } from "../../lib/authStore";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { useDashboardSummary } from "../../hooks/usePaymentQueries";
import { useTenantDashboardOverview } from "../../hooks/useTenantDashboard";
import {
  eurIsPositive,
  formatEurDashboard,
  formatKcDashboard,
  type ClosedMonthRow,
  type ExportFormat,
  type TimeRange,
} from "../../schemas/tenantDashboard";

/**
 * UC-004 Tenant dashboard — per-role render split (Phase 19 + Phase 23).
 *
 * Per-role detect:
 *   - role === "endUser"  → Milan/Zdeněk path: <ActionCardKolikDlulis> top,
 *     drill-down Modal (desktop) / BottomSheet (mobile). UC-004.
 *   - role === "subAdmin" → Milan path: Action Card + microgroup hint.
 *   - role === "tenantAdmin" → David A&L soft path: UC-005 full dashboard —
 *     3× Stat + <TimelineChart> + <SpzBarChart> + <ClosedMonthsTable> +
 *     export panel. Mobile breakpoint switches chart to aggregated mode +
 *     renders <SpzMobileBreakdownTable> beneath.
 *
 * Per princip #6 — frontend NIKDY nepředpokládá tenant scope; backend RLS
 * enforce-uje izolaci. Cross-tenant attempts vrací HTTP 403 + TENANT_ACCESS_DENIED
 * (REQ-EH-007); apiClient surfacuje error path.
 */
export const Route = createRoute({
  getParentRoute: () => TenantLayoutRoute,
  path: "/tenant/dashboard",
  component: TenantDashboard,
});

function TenantDashboard() {
  const auth = useAuthStore((s) => s.auth);
  const role = auth.state === "authenticated" ? auth.user.role : "endUser";

  if (role === "tenantAdmin") {
    return <DavidDashboard />;
  }
  return <MilanZdenekDashboard />;
}

/**
 * Milan/Zdeněk dashboard — primární UC-004 view.
 * subAdmin (Milan) vidí stejný layout jako endUser (Zdeněk), navíc má v
 * sidebar microgroup management (řešeno v Phase 11 layout, ne zde).
 */
function MilanZdenekDashboard() {
  const { t } = useTranslation("tenant");
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === "mobile";
  const [drillDownOpen, setDrillDownOpen] = useState(false);
  const query = useDashboardSummary();

  if (query.isLoading) {
    return (
      <div className="flex flex-col gap-4" data-testid="tenant-dashboard-loading">
        <LoadingIndicator />
      </div>
    );
  }

  const summary = query.data ?? {
    open_intent_count: 0,
    open_total_kc: "0",
    next_intent: null,
    is_a_l_soft: false,
  };

  const drillDownContent = (
    <div className="flex flex-col gap-2" data-testid="drill-down-summary">
      <p>
        <strong>{t("payment.actionCard.openCount")}:</strong>{" "}
        {summary.open_intent_count}
      </p>
      <p>
        <strong>{t("payment.amount")}:</strong> {summary.open_total_kc} Kč
      </p>
      {summary.next_intent && (
        <p>
          {t("payment.title")} {summary.next_intent.month} —{" "}
          {summary.next_intent.amount_kc} Kč
        </p>
      )}
      <p className="text-tertiary">
        Pro detail QR a kalkulace klikněte na "Zaplatit nyní".
      </p>
    </div>
  );

  return (
    <div className="flex flex-col gap-4" data-testid="tenant-dashboard" data-role="milan-zdenek">
      <header className="flex flex-col gap-2">
        <h1 className="m-0 text-2xl font-bold text-primary">{t("dashboard.title")}</h1>
        <p className="m-0 text-tertiary">{t("dashboard.description")}</p>
      </header>

      <ActionCardKolikDlulis
        openTotalKc={summary.open_total_kc}
        openIntentCount={summary.open_intent_count}
        nextIntentId={summary.next_intent?.payment_intent_id ?? null}
        onDrillDown={
          summary.open_intent_count > 0
            ? () => setDrillDownOpen(true)
            : undefined
        }
      />

      {query.isError && (
        <InlineAlert variant="warning" title="Načítání úhrad selhalo">
          <p>Zkuste obnovit stránku. Data se aktualizují automaticky.</p>
        </InlineAlert>
      )}

      {isMobile ? (
        <BottomSheet
          open={drillDownOpen}
          title={t("payment.actionCard.drillDownTitle")}
          onClose={() => setDrillDownOpen(false)}
        >
          {drillDownContent}
        </BottomSheet>
      ) : (
        <Modal
          open={drillDownOpen}
          title={t("payment.actionCard.drillDownTitle")}
          onClose={() => setDrillDownOpen(false)}
        >
          {drillDownContent}
        </Modal>
      )}
    </div>
  );
}

/**
 * David A&L soft dashboard — Phase 23 UC-005 full implementation.
 *
 * Layout:
 *   - Header s tenant názvem + TimeRangeSelector.
 *   - 3× StatCard (consumption / payments / outstanding).
 *   - EUR widget conditional (REQ-BIZ-023) — pouze pokud consumption_eur > 0.
 *   - <TimelineChart> per-SPZ multi-line (desktop) / aggregated (mobile).
 *   - <SpzMobileBreakdownTable> visible jen mobile (sibling pod chart).
 *   - <SpzBarChart> per-SPZ bar chart.
 *   - <ClosedMonthsTable> data-table/card-list per breakpoint.
 *   - Export panel (XLSX/CSV/JSON radio + Stáhnout button).
 */
function DavidDashboard() {
  const { t } = useTranslation("tenant");
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === "mobile";
  const [range, setRange] = useState<TimeRange>("12M");
  const [exportFormat, setExportFormat] = useState<ExportFormat>("xlsx");
  const [exportNotice, setExportNotice] = useState<string | null>(null);
  const query = useTenantDashboardOverview(range);

  const data = query.data;

  const sumEurPositive = useMemo(
    () => (data ? eurIsPositive(data.consumption_total_eur) : false),
    [data],
  );

  const handleExport = (): void => {
    if (!data) return;
    const url = `/api/tenant/dashboard/export?range=${encodeURIComponent(range)}&format=${exportFormat}`;
    if (typeof window !== "undefined") {
      window.location.assign(url);
    }
    setExportNotice(
      t("dashboard.david.export.success").replace("%format%", exportFormat.toUpperCase()),
    );
  };

  const handleDownloadPdf = (row: ClosedMonthRow): void => {
    if (!row.composite_pdf_id) return;
    const url = `/api/tenant/months/${row.budget_id}/composite-pdf`;
    if (typeof window !== "undefined") {
      window.location.assign(url);
    }
  };

  return (
    <div
      className="flex flex-col gap-4"
      data-testid="tenant-dashboard"
      data-role="david"
    >
      <header className="flex flex-col gap-2">
        <h1 className="m-0 text-2xl font-bold text-primary">{t("dashboard.david.title")}</h1>
        <p className="m-0 text-tertiary">{t("dashboard.david.lead")}</p>
        <TimeRangeSelector
          value={range}
          onChange={setRange}
          ariaLabel={t("dashboard.david.timeline.rangeLabel")}
        />
      </header>

      {query.isLoading && (
        <div data-testid="david-dashboard-loading">
          <LoadingIndicator />
          <p>{t("dashboard.david.loading")}</p>
        </div>
      )}

      {query.isError && (
        <InlineAlert variant="warning" title={t("dashboard.david.loadingError")}>
          <p data-testid="david-dashboard-error">
            {query.error instanceof Error ? query.error.message : String(query.error ?? "")}
          </p>
        </InlineAlert>
      )}

      {data && (
        <>
          {data.closed_months.length === 0 && (
            <InlineAlert variant="info" title="Zatím tu nejsou žádná data">
              Váš účet je aktivní. Jakmile vám správce přiřadí tankovací karty a
              schválí měsíční rozpočet, uvidíte tady svoji spotřebu, úhrady (QR
              kód) a měsíční přehledy k vyúčtování.
            </InlineAlert>
          )}
          <section
            className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3"
            data-testid="david-stats"
            aria-label={t("dashboard.david.title")}
          >
            <StatCard
              label={t("dashboard.david.stats.consumption")}
              value={formatKcDashboard(data.consumption_total_kc)}
              helper={t("dashboard.david.stats.consumptionHelper")}
            />
            <StatCard
              label={t("dashboard.david.stats.payments")}
              value={formatKcDashboard(data.payments_total_kc)}
              helper={t("dashboard.david.stats.paymentsHelper")}
            />
            <StatCard
              label={t("dashboard.david.stats.outstanding")}
              value={formatKcDashboard(data.outstanding_total_kc)}
              helper={t("dashboard.david.stats.outstandingHelper")}
            />
          </section>

          {sumEurPositive && (
            <Card data-testid="david-eur-widget">
              <CardHeader title={t("dashboard.david.eur.title")} />
              <CardBody>
                <p>{t("dashboard.david.eur.body")}</p>
                <p>
                  <strong>{t("dashboard.david.eur.value")}:</strong>{" "}
                  <span data-testid="david-eur-value">
                    {formatEurDashboard(data.consumption_total_eur)}
                  </span>
                </p>
              </CardBody>
            </Card>
          )}

          <Card>
            <CardHeader title={t("dashboard.david.timeline.title")} />
            <CardBody>
              <Suspense fallback={<div className="h-80 animate-pulse bg-secondary rounded" aria-hidden="true" />}>
                <TimelineChart points={data.timeline} />
              </Suspense>
              {isMobile && (
                <SpzMobileBreakdownTable
                  points={data.spz_breakdown}
                  showEur={sumEurPositive}
                />
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title={t("dashboard.david.spzBar.title")} />
            <CardBody>
              <Suspense fallback={<div className="h-80 animate-pulse bg-secondary rounded" aria-hidden="true" />}>
                <SpzBarChart points={data.spz_breakdown} />
              </Suspense>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title={t("dashboard.david.closedMonths.title")} />
            <CardBody>
              <ClosedMonthsTable
                rows={data.closed_months}
                onDownloadPdf={handleDownloadPdf}
              />
            </CardBody>
          </Card>

          <Card data-testid="david-export-panel">
            <CardHeader title={t("dashboard.david.export.title")} />
            <CardBody>
              <ExportFormatRadioGroup
                value={exportFormat}
                onChange={setExportFormat}
              />
              <div className="mt-3 flex justify-end gap-2">
                <Button
                  color="primary"
                  onPress={handleExport}
                  data-testid="david-export-submit"
                >
                  {t("dashboard.david.export.submit")}
                </Button>
              </div>
              {exportNotice && (
                <p
                  className="mt-2 text-success-primary"
                  data-testid="david-export-notice"
                  role="status"
                  aria-live="polite"
                >
                  {exportNotice}
                </p>
              )}
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
}
